import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import Particle from "../Particle";
import logo from "../../Assets/0xfix.png";

function useScrollAnimation() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`scroll-animate ${isVisible ? "animate-in" : ""} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function Staking() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const features = [
    { icon: "🔒", title: "Secure Staking", desc: "Industry-leading security measures" },
    { icon: "📈", title: "High Rewards", desc: "Competitive APY rates" },
    { icon: "⚡", title: "Instant Unstake", desc: "Flexible withdrawal options" },
    { icon: "🌐", title: "Multi-Chain", desc: "Support for multiple networks" },
  ];

  const stats = [
    { value: "$2B+", label: "Total Staked" },
    { value: "50K+", label: "Active Stakers" },
    { value: "99.9%", label: "Uptime" },
    { value: "24/7", label: "Support" },
  ];

  return (
    <Container fluid className="staking-section">
      <Particle />
      
      <Container className="staking-container">
        <AnimatedSection className="staking-header">
          <span className="coming-soon-badge">
            <span className="pulse-dot"></span>
            Coming Soon
          </span>
          <h1 className="staking-title">
            <span className="gradient-text">Staking</span> Platform
          </h1>
          <p className="staking-subtitle">
            Earn rewards by staking your assets. Secure, reliable, and profitable.
          </p>
        </AnimatedSection>

        <Row className="align-items-center">
          <Col lg={6} md={12}>
            <AnimatedSection delay={200} className="staking-visual">
              <div className="staking-orb-container" style={{
                transform: `translate(${mousePos.x * -0.5}px, ${mousePos.y * -0.5}px)`
              }}>
                <div className="orb-core">
                  <img src={logo} alt="0xFix" className="orb-logo" />
                </div>
                <div className="orb-ring ring-1"></div>
                <div className="orb-ring ring-2"></div>
                <div className="orb-ring ring-3"></div>
                
                <div className="floating-stats">
                  <div className="float-stat fs-1">
                    <span>📈</span>
                    <span>+12% APY</span>
                  </div>
                  <div className="float-stat fs-2">
                    <span>🔒</span>
                    <span>Secure</span>
                  </div>
                  <div className="float-stat fs-3">
                    <span>⚡</span>
                    <span>Fast</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </Col>

          <Col lg={6} md={12}>
            <AnimatedSection delay={300} className="features-section">
              <h2 className="features-title">Why Stake With Us?</h2>
              <div className="features-list">
                {features.map((feature, index) => (
                  <AnimatedSection key={index} delay={400 + index * 100}>
                    <div className="feature-row">
                      <div className="feature-icon">{feature.icon}</div>
                      <div className="feature-info">
                        <h4>{feature.title}</h4>
                        <p>{feature.desc}</p>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </AnimatedSection>
          </Col>
        </Row>

        <AnimatedSection delay={500} className="stats-section">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection delay={600} className="cta-section">
          <div className="staking-cta">
            <h2>Be the first to know</h2>
            <p>Join our waitlist to get notified when staking launches</p>
            <div className="cta-form">
              <input type="email" placeholder="Enter your email" className="email-input" />
              <Button className="notify-btn">Notify Me</Button>
            </div>
          </div>
        </AnimatedSection>
      </Container>
    </Container>
  );
}

export default Staking;
